/**
 * CRM 状态管理
 *
 * 本模块实现了 CRM 前端的全局状态管理，采用轻量级的自定义 Store 模式
 *（非 Redux/Pinia 等框架），通过 subscribe/notify 机制实现状态变更通知。
 *
 * 包含三个 Store：
 * - customerStore：客户列表与选中客户的状态管理
 * - emailStore：邮件列表、邮箱账户、邮件对话、邮件标签、邮件签名等综合状态
 * - taskStore：异步任务（如邮件抓取）的状态跟踪
 *
 * authStore 已从 @crm/shared 导入并重导出，因为认证状态需要在 frontend 和 mobile 之间共享。
 *
 * Store 模式说明：
 * - 每个 store 持有单一 state 对象，通过 mutation 方法修改
 * - 组件通过 subscribe() 注册监听器，state 变更时通过 notify() 通知所有监听器
 * - subscribe() 返回取消订阅函数，组件在 disconnectedCallback 中调用以避免内存泄漏
 * - 所有异步操作方法内部处理 loading 状态和错误捕获，调用方无需额外处理
 *
 * 关联模块：
 * - ./api.js — 各 API 命名空间（customerApi、emailApi 等）
 * - ./types.js — TypeScript 类型定义
 * - @crm/shared/stores/auth.js — 共享的认证 Store
 */

import { authStore } from '@crm/shared/stores/auth.js';
import { customerApi, emailApi, emailAccountApi, emailThreadApi, emailTagApi, signatureApi, taskApi } from './api.js';
import type { Customer, Email, EmailAccount, EmailTag, ThreadSummary, TaskStatus, EmailSignature, CreateEmailSignatureRequest, UpdateEmailSignatureRequest, ActiveSignature } from './types.js';

// Auth Store 从 @crm/shared 重导出
// 认证 Store 在 packages/shared 中实现，供 frontend 和 mobile 共享使用
export { authStore };

// ==================== Customer Store ====================

/**
 * 客户 Store 状态接口
 */
interface CustomerState {
  /** 当前页面的客户列表 */
  customers: Customer[];
  /** 客户总数（服务端返回，用于分页） */
  total: number;
  /** 当前页码 */
  currentPage: number;
  /** 每页条数 */
  perPage: number;
  /** 是否正在加载（用于显示 loading 指示器） */
  isLoading: boolean;
  /** 当前选中的客户（详情面板或编辑表单使用） */
  selectedCustomer: Customer | null;
}

/**
 * 客户 Store
 *
 * 管理客户列表的 CRUD 操作和分页状态。
 * 组件通过 subscribe() 监听状态变化，store 在数据变更后通过 notify() 通知。
 */
export const customerStore = {
  state: {
    customers: [] as Customer[],
    total: 0,
    currentPage: 1,
    perPage: 20,
    isLoading: false,
    selectedCustomer: null as Customer | null,
  } as CustomerState,

  /** 客户列表请求序号，仅允许最后一次请求更新共享状态。 */
  _listRequestId: 0,

  /** 状态变更监听器列表 */
  listeners: [] as Array<(state: CustomerState) => void>,

  /**
   * 订阅状态变更
   *
   * @param listener - 状态变更回调函数，接收当前 state 作为参数
   * @returns 取消订阅函数，调用后移除该监听器
   */
  subscribe(listener: (state: CustomerState) => void) {
    this.listeners.push(listener);
    // 订阅时立即触发一次，使组件获得初始状态
    listener(this.state);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  },

  /** 通知所有监听器状态已变更 */
  notify() {
    this.listeners.forEach((listener) => listener(this.state));
  },

  /**
   * 获取客户列表
   *
   * 向服务端请求客户数据并更新 state。支持分页和多种筛选条件。
   *
   * @param params - 查询参数（均为可选）
   * @param params.page - 页码
   * @param params.per_page - 每页条数
   * @param params.company_name - 公司名称模糊搜索
   * @param params.email - 邮箱地址筛选
   * @param params.keyword - 通用关键词搜索
   * @param params.category_id - 客户分类 ID 筛选
   * @param params.nation_id - 国家 ID 筛选
   * @param params.has_email - 是否有邮箱（1=有，0=无）
   * @param params.created_start - 创建时间下限（Unix 秒，含）
   * @param params.created_end - 创建时间上限（Unix 秒，含）
   * @sideeffect 设置 isLoading 状态，更新 customers/total/currentPage/perPage
   */
  async fetchCustomers(params?: {
    page?: number;
    per_page?: number;
    company_name?: string;
    email?: string;
    keyword?: string;
    category_id?: number;
    nation_id?: number;
    has_email?: number;
    created_start?: number;
    created_end?: number;
  }) {
    const requestId = ++this._listRequestId;
    this.state.isLoading = true;
    this.notify();

    try {
      const response = await customerApi.list(params);
      if (requestId !== this._listRequestId) return;
      this.state.customers = response.data;
      this.state.total = response.pagination.total;
      this.state.currentPage = response.pagination.current_page;
      this.state.perPage = response.pagination.per_page;
    } catch (error) {
      if (requestId === this._listRequestId) {
        console.error('Failed to fetch customers:', error);
      }
    } finally {
      if (requestId === this._listRequestId) {
        this.state.isLoading = false;
        this.notify();
      }
    }
  },

  /**
   * 创建新客户
   *
   * @param data - 客户信息（所有字段均为可选）
   * @returns 创建成功的客户对象（含服务端生成的 ID）
   * @sideeffect 将新客户插入到列表头部，total 递增
   */
  async createCustomer(data: {
    company_name?: string;
    name?: string;
    job_title?: string;
    email?: string;
    phone?: string;
    address?: string;
    remark?: string;
    nation_id?: number;
    website?: string;
  }) {
    const customer = await customerApi.create(data);
    // 新客户插入到列表头部，确保用户立即看到
    this.state.customers.unshift(customer);
    this.state.total++;
    this.notify();
    return customer;
  },

  /**
   * 更新客户信息
   *
   * @param id - 客户 ID
   * @param data - 需要更新的字段（部分更新）
   * @returns 更新后的完整客户对象
   * @sideeffect 同步更新列表中的对应记录和 selectedCustomer（如果正在选中该客户）
   */
  async updateCustomer(
    id: number,
    data: Partial<{
      company_name: string;
      name: string;
      job_title: string;
      email: string;
      phone: string;
      address: string;
      remark: string;
    }>
  ) {
    const customer = await customerApi.update(id, data);
    // 在列表中找到对应记录并替换
    const index = this.state.customers.findIndex((c) => c.id === id);
    if (index !== -1) {
      this.state.customers[index] = customer;
    }
    // 如果当前选中的就是这个客户，同步更新选中状态
    if (this.state.selectedCustomer?.id === id) {
      this.state.selectedCustomer = customer;
    }
    this.notify();
    return customer;
  },

  /**
   * 删除客户（软删除）
   *
   * @param id - 要删除的客户 ID
   * @sideeffect 从列表中移除该客户，total 递减，清除 selectedCustomer（如果选中）
   */
  async deleteCustomer(id: number) {
    await customerApi.delete(id);
    this.state.customers = this.state.customers.filter((c) => c.id !== id);
    this.state.total--;
    if (this.state.selectedCustomer?.id === id) {
      this.state.selectedCustomer = null;
    }
    this.notify();
  },

  /**
   * 设置当前选中的客户
   *
   * @param customer - 要选中的客户对象，传入 null 取消选中
   * @sideeffect 更新 selectedCustomer 并通知监听器
   */
  selectCustomer(customer: Customer | null) {
    this.state.selectedCustomer = customer;
    this.notify();
  },
};

// ==================== Email Store ====================

/**
 * 邮件 Store 状态接口
 *
 * 邮件模块状态较为复杂，包含列表视图、对话线程视图、文件夹计数、
 * 邮箱账户、邮件标签、邮件签名等多个维度的状态管理。
 */
interface EmailState {
  /** 当前页面的邮件列表 */
  emails: Email[];
  /** 邮件总数（服务端返回，用于分页） */
  total: number;
  /** 当前页码 */
  currentPage: number;
  /** 每页条数 */
  perPage: number;
  /** 是否正在加载 */
  isLoading: boolean;
  /** 邮箱账户列表（IMAP/SMTP 配置） */
  accounts: EmailAccount[];
  // Folder state — 文件夹维度状态
  /** 当前选中的文件夹（inbox/sent/drafts/trash 等） */
  currentFolder: string;
  /** 各文件夹的邮件计数（总数和未读数） */
  folderCounts: Array<{ folder: string; total: number; unread: number }>;
  // Thread state — 对话线程维度状态
  /** 对话线程列表 */
  threads: ThreadSummary[];
  /** 线程总数 */
  threadTotal: number;
  /** 线程列表当前页码 */
  threadCurrentPage: number;
  /** 邮件视图模式：'list' 普通列表 / 'thread' 对话线程 */
  threadViewMode: 'list' | 'thread';
  /** 当前选中的线程 ID */
  selectedThreadId: string | null;
  /** 当前选中线程内的邮件列表 */
  threadEmails: Email[];
  /** 邮件标签列表 */
  tags: EmailTag[];
  /** 当前选中的标签 ID（用于按标签筛选） */
  currentTagId: number | null;
  /** 邮件签名列表，按 account_id 分组存储 */
  signatures: Record<number, EmailSignature[]>;
  /** 各邮箱账户的当前激活签名，按 account_id 分组 */
  activeSignature: Record<number, ActiveSignature | null>;
}

/**
 * 邮件 Store
 *
 * 管理邮件模块的所有状态和操作，包括：
 * - 邮件列表的获取、已读标记、删除、恢复
 * - 邮箱账户的管理（创建、同步开关、状态切换、删除）
 * - 邮件对话线程的获取和查看
 * - 邮件标签的 CRUD 和邮件标签关联
 * - 邮件签名的 CRUD 和激活/停用
 * - 文件夹计数的维护
 *
 * 这是前端最复杂的 Store，因为邮件模块涉及多个关联维度的状态。
 */
export const emailStore = {
  state: {
    emails: [] as Email[],
    total: 0,
    currentPage: 1,
    perPage: 20,
    isLoading: false,
    accounts: [] as EmailAccount[],
    currentFolder: 'inbox',            // 默认文件夹为收件箱
    folderCounts: [] as Array<{ folder: string; total: number; unread: number }>,
    threads: [] as ThreadSummary[],
    threadTotal: 0,
    threadCurrentPage: 1,
    threadViewMode: 'list' as 'list' | 'thread',  // 默认普通列表视图
    selectedThreadId: null,
    threadEmails: [] as Email[],
    tags: [] as EmailTag[],
    currentTagId: null,
    signatures: {} as Record<number, EmailSignature[]>,
    activeSignature: {} as Record<number, ActiveSignature | null>,
  } as EmailState,

  /** 邮件列表请求序号，仅允许最后一次请求更新共享状态。 */
  _listRequestId: 0,

  /** 状态变更监听器列表 */
  listeners: [] as Array<(state: EmailState) => void>,

  /**
   * 订阅状态变更
   *
   * @param listener - 状态变更回调函数，接收当前 state 作为参数
   * @returns 取消订阅函数
   */
  subscribe(listener: (state: EmailState) => void) {
    this.listeners.push(listener);
    listener(this.state);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  },

  /** 通知所有监听器状态已变更 */
  notify() {
    this.listeners.forEach((listener) => listener(this.state));
  },

  /**
   * 获取邮件列表
   *
   * @param params - 查询参数（均为可选）
   * @param params.page - 页码
   * @param params.per_page - 每页条数
   * @param params.customer_id - 按客户 ID 筛选
   * @param params.account_id - 按邮箱账户筛选
   * @param params.is_read - 按已读状态筛选（0=未读，1=已读）
   * @param params.folder - 按文件夹筛选（inbox/sent/drafts/trash）
   * @param params.tag_id - 按标签 ID 筛选
   * @param params.is_starred - 按星标状态筛选
   * @sideeffect 设置 isLoading，更新 emails/total/currentPage/perPage
   */
  async fetchEmails(params?: {
    page?: number;
    per_page?: number;
    customer_id?: number;
    account_id?: number;
    is_read?: number;
    folder?: string;
    tag_id?: number;
    is_starred?: number;
  }) {
    const requestId = ++this._listRequestId;
    this.state.isLoading = true;
    this.notify();

    try {
      const response = await emailApi.list(params);
      if (requestId !== this._listRequestId) return;
      this.state.emails = response.data;
      this.state.total = response.pagination.total;
      this.state.currentPage = response.pagination.current_page;
      this.state.perPage = response.pagination.per_page;
    } catch (error) {
      if (requestId === this._listRequestId) {
        console.error('Failed to fetch emails:', error);
      }
    } finally {
      if (requestId === this._listRequestId) {
        this.state.isLoading = false;
        this.notify();
      }
    }
  },

  /**
   * 清空邮件列表
   *
   * 在切换文件夹或账户时清空当前列表，避免显示旧数据。
   * @sideeffect 清空 emails 数组，重置 total 为 0
   */
  clearList() {
    this.state.emails = [];
    this.state.total = 0;
    this.notify();
  },

  /**
   * 获取邮箱账户列表
   *
   * 一次性获取最多 100 个账户（per_page=100），用于账户选择下拉框。
   * @sideeffect 更新 accounts 数组
   */
  async fetchAccounts() {
    try {
      const response = await emailAccountApi.list({ per_page: 100 });
      this.state.accounts = response.data;
      this.notify();
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  },

  /**
   * 标记邮件为已读/未读（切换状态）
   *
   * 调用 API 切换 is_read 状态后，同步更新本地 state：
   * 1. 更新 emails 列表中的对应记录
   * 2. 如果该邮件在 threadEmails 中，也同步更新
   * 3. 更新所属线程的 has_unread 状态
   * 4. 更新文件夹未读计数（根据 is_read 新值计算 delta）
   *
   * @param id - 邮件 ID
   * @returns 更新后的邮件对象
   */
  async markAsRead(id: number) {
    const email = await emailApi.markAsRead(id);
    const index = this.state.emails.findIndex((e) => e.id === id);
    if (index !== -1) {
      // 使用不可变更新方式，确保 Lit 能检测到变化
      this.state.emails = [
        ...this.state.emails.slice(0, index),
        email,
        ...this.state.emails.slice(index + 1),
      ];
    }
    // 同步更新 threadEmails（对话视图依赖此数组）
    const threadIndex = this.state.threadEmails.findIndex((e) => e.id === id);
    if (threadIndex !== -1) {
      this.state.threadEmails = [
        ...this.state.threadEmails.slice(0, threadIndex),
        email,
        ...this.state.threadEmails.slice(threadIndex + 1),
      ];
      // 更新线程 has_unread 状态
      const threadId = email.thread_id;
      if (threadId) {
        const hasUnread = this.state.threadEmails.some(
          e => e.thread_id === threadId && e.is_read === 0
        );
        this.state.threads = this.state.threads.map(t =>
          t.thread_id === threadId ? { ...t, is_unread: hasUnread } : t
        );
      }
    }
    // 更新文件夹未读计数：markAsRead 会切换 is_read 状态
    const folder = email.folder || 'inbox';
    // is_read=1 表示变为已读 → 未读数 -1；is_read=0 表示变为未读 → 未读数 +1
    const delta = email.is_read === 1 ? -1 : 1;
    this.state.folderCounts = this.state.folderCounts.map(c =>
      c.folder === folder ? { ...c, unread: Math.max(0, c.unread + delta) } : c
    );
    this.notify();
    return email;
  },

  /**
   * 批量标记邮件为已读
   *
   * 批量更新本地 state 后，从服务端重新获取文件夹计数，
   * 因为在 thread 视图下被标记的邮件不一定都在 state.emails 中，
   * 本地无法可靠计算未读数的变化量。
   *
   * @param ids - 要标记为已读的邮件 ID 数组
   * @sideeffect 更新 emails/threadEmails 中的 is_read，刷新文件夹计数
   */
  async markMultipleAsRead(ids: number[]) {
    await emailApi.markMultipleAsRead(ids);
    const idSet = new Set(ids);
    // 批量更新本地 state 中的邮件已读状态
    this.state.emails = this.state.emails.map(e =>
      idSet.has(e.id) ? { ...e, is_read: 1 } : e
    );
    this.state.threadEmails = this.state.threadEmails.map(e =>
      idSet.has(e.id) ? { ...e, is_read: 1 } : e
    );
    // 更新线程 has_unread 状态
    const affectedThreads = new Set(
      this.state.threadEmails.filter(e => idSet.has(e.id) && e.thread_id).map(e => e.thread_id)
    );
    for (const tid of affectedThreads) {
      const hasUnread = this.state.threadEmails.some(
        e => e.thread_id === tid && e.is_read === 0
      );
      this.state.threads = this.state.threads.map(t =>
        t.thread_id === tid ? { ...t, is_unread: hasUnread } : t
      );
    }
    // 文件夹未读计数从服务端拉权威值（thread 视图下被标记的邮件不一定在 state.emails 里，
    // 本地无法可靠计算 delta）
    try {
      const counts = await emailApi.getFolderCounts();
      this.state.folderCounts = counts;
    } catch { /* keep current counts on failure */ }
    this.notify();
  },

  /**
   * 删除邮件
   *
   * 用户删除操作：始终将邮件移入 trash 文件夹。
   * 如果邮件已在 trash 中，则执行永久删除。
   * 管理员后台删除（is_deleted=1）走独立的管理接口，不在此处理。
   *
   * @param id - 要删除的邮件 ID
   * @sideeffect 从列表中移除（非 trash 文件夹下），刷新文件夹计数
   */
  async deleteEmail(id: number) {
    // 用户删除：始终移入 trash，is_deleted 保持不变
    // 管理员后台删除（is_deleted=1）走独立管理接口
    const email = this.state.emails.find(e => e.id === id);
    if (email?.folder === 'trash') {
      // 已在垃圾箱中 → 永久删除
      await emailApi.delete(id);
      this.fetchFolderCounts();
      return;
    }
    // 不在垃圾箱中 → 移入垃圾箱（folder → trash）
    await emailApi.delete(id);
    if (this.state.currentFolder !== 'trash') {
      // 当前不在垃圾箱视图 → 从列表中移除
      this.state.emails = this.state.emails.filter((e) => e.id !== id);
      this.state.total--;
    }
    this.fetchFolderCounts();
    this.notify();
  },

  /**
   * 恢复邮件（从垃圾箱移回收件箱）
   *
   * @param id - 要恢复的邮件 ID
   * @sideeffect 从当前列表移除，更新文件夹计数（trash -1, inbox +1）
   */
  async restoreEmail(id: number) {
    await emailApi.restore(id);
    // 从当前列表移除（通常在 trash 视图下操作）
    this.state.emails = this.state.emails.filter((e) => e.id !== id);
    this.state.total--;
    // 更新文件夹计数：垃圾箱总数 -1，收件箱总数 +1
    this.state.folderCounts = this.state.folderCounts.map(c => {
      if (c.folder === 'trash') return { ...c, total: c.total - 1, unread: c.unread };
      if (c.folder === 'inbox') return { ...c, total: c.total + 1 };
      return c;
    });
    this.notify();
  },

  /**
   * 设置当前文件夹并清除标签筛选
   *
   * @param folder - 文件夹名称（inbox/sent/drafts/trash 等）
   * @sideeffect 更新 currentFolder，重置 currentTagId 为 null
   */
  setCurrentFolder(folder: string) {
    this.state.currentFolder = folder;
    // 切换文件夹时清除标签筛选，避免跨文件夹的标签条件残留
    this.state.currentTagId = null;
    this.notify();
  },

  /**
   * 设置当前标签筛选 ID
   *
   * @param tagId - 标签 ID，传入 null 表示取消标签筛选
   * @sideeffect 更新 currentTagId
   */
  setCurrentTagId(tagId: number | null) {
    this.state.currentTagId = tagId;
    this.notify();
  },

  /**
   * 更新邮件星标状态（乐观更新）
   *
   * 直接在本地更新星标状态，不等待服务端确认。
   *
   * @param emailId - 邮件 ID
   * @param isStarred - 星标状态（1=已加星，0=未加星）
   */
  updateEmailStarred(emailId: number, isStarred: number) {
    this.state.emails = this.state.emails.map(e =>
      e.id === emailId ? { ...e, is_starred: isStarred } : e
    );
    this.notify();
  },

  /**
   * 获取文件夹计数
   *
   * 从服务端获取各文件夹的邮件总数和未读数，用于侧边栏文件夹显示。
   * @sideeffect 更新 folderCounts 数组
   */
  async fetchFolderCounts() {
    try {
      const counts = await emailApi.getFolderCounts();
      this.state.folderCounts = counts;
      this.notify();
    } catch (error) {
      console.error('Failed to fetch folder counts:', error);
    }
  },

  /**
   * 创建邮箱账户
   *
   * @param data - 邮箱账户配置
   * @param data.address - 邮箱地址
   * @param data.password - 邮箱密码
   * @param data.imap - IMAP 服务器配置（可选）
   * @param data.smtp - SMTP 服务器配置（可选）
   * @param data.status - 账户状态（可选）
   * @returns 创建成功的邮箱账户对象
   * @sideeffect 将新账户添加到 accounts 列表
   */
  async createAccount(data: {
    address: string;
    password: string;
    imap?: string;
    smtp?: string;
    status?: number;
  }) {
    const account = await emailAccountApi.create(data);
    this.state.accounts.push(account);
    this.notify();
    return account;
  },

  /**
   * 切换邮箱账户的同步开关
   *
   * @param id - 账户 ID
   * @param sync_enabled - 同步状态（1=开启，0=关闭）
   * @sideeffect 更新 accounts 中对应账户的 sync_enabled 字段
   */
  async toggleAccountSync(id: number, sync_enabled: number) {
    await emailAccountApi.toggleSync(id, sync_enabled);
    const index = this.state.accounts.findIndex(a => a.id === id);
    if (index !== -1) {
      this.state.accounts = [
        ...this.state.accounts.slice(0, index),
        { ...this.state.accounts[index], sync_enabled },
        ...this.state.accounts.slice(index + 1),
      ];
    }
    this.notify();
  },

  /**
   * 切换邮箱账户的状态（启用/禁用）
   *
   * @param id - 账户 ID
   * @param status - 状态值（1=启用，0=禁用）
   * @sideeffect 更新 accounts 中对应账户的 status 字段
   */
  async toggleAccountStatus(id: number, status: number) {
    await emailAccountApi.toggleStatus(id, status);
    const index = this.state.accounts.findIndex(a => a.id === id);
    if (index !== -1) {
      this.state.accounts = [
        ...this.state.accounts.slice(0, index),
        { ...this.state.accounts[index], status },
        ...this.state.accounts.slice(index + 1),
      ];
    }
    this.notify();
  },

  /**
   * 删除邮箱账户
   *
   * @param id - 要删除的账户 ID
   * @sideeffect 从 accounts 列表中移除该账户
   */
  async deleteAccount(id: number) {
    await emailAccountApi.delete(id);
    this.state.accounts = this.state.accounts.filter((a) => a.id !== id);
    this.notify();
  },

  // ==================== 对话线程方法 ====================

  /**
   * 获取对话线程列表
   *
   * @param params - 查询参数（均为可选）
   * @param params.page - 页码
   * @param params.per_page - 每页条数
   * @param params.account_id - 按邮箱账户筛选
   * @sideeffect 设置 isLoading，更新 threads/threadTotal/threadCurrentPage
   */
  async fetchThreads(params?: { page?: number; per_page?: number; account_id?: number }) {
    this.state.isLoading = true;
    this.notify();
    try {
      const response = await emailThreadApi.list(params);
      this.state.threads = response.data;
      this.state.threadTotal = response.pagination.total;
      this.state.threadCurrentPage = response.pagination.current_page;
    } catch (error) {
      console.error('Failed to fetch threads:', error);
    } finally {
      this.state.isLoading = false;
      this.notify();
    }
  },

  /**
   * 获取指定线程的邮件列表
   *
   * @param threadId - 线程 ID
   * @sideeffect 设置 selectedThreadId，更新 threadEmails
   */
  async fetchThread(threadId: string) {
    try {
      const response = await emailThreadApi.getThread(threadId);
      this.state.selectedThreadId = threadId;
      this.state.threadEmails = response.messages;
      this.notify();
    } catch (error) {
      console.error('Failed to fetch thread:', error);
    }
  },

  /**
   * 通过邮件 ID 查找其所属线程并获取线程内所有邮件
   *
   * @param emailId - 邮件 ID
   * @sideeffect 设置 selectedThreadId，更新 threadEmails
   */
  async fetchEmailThread(emailId: number) {
    try {
      const response = await emailThreadApi.getEmailThread(emailId);
      this.state.selectedThreadId = response.thread_id;
      this.state.threadEmails = response.messages;
      this.notify();
    } catch (error) {
      console.error('Failed to fetch email thread:', error);
    }
  },

  // ==================== 邮件标签方法 ====================

  /**
   * 获取所有邮件标签
   * @sideeffect 更新 tags 数组
   */
  async fetchTags() {
    try {
      this.state.tags = await emailTagApi.list();
      this.notify();
    } catch (e) { console.error('Failed to fetch tags:', e); }
  },

  /**
   * 创建邮件标签
   *
   * @param data - 标签信息
   * @param data.name - 标签名称
   * @param data.color - 标签颜色
   * @returns 创建成功的标签对象
   * @sideeffect 将新标签添加到 tags 数组
   */
  async createTag(data: { name: string; color: string }) {
    const tag = await emailTagApi.create(data);
    // 补充 uid 和 sort_order 默认值（服务端可能不返回这些字段）
    this.state.tags = [...this.state.tags, { ...tag, uid: 0, sort_order: 0 }];
    this.notify();
    return tag;
  },

  /**
   * 更新邮件标签
   *
   * @param id - 标签 ID
   * @param data - 需要更新的字段（name 和/或 color）
   * @sideeffect 更新 tags 数组中对应标签
   */
  async updateTag(id: number, data: { name?: string; color?: string }) {
    const tag = await emailTagApi.update(id, data);
    this.state.tags = this.state.tags.map(t => t.id === id ? { ...t, ...tag } : t);
    this.notify();
  },

  /**
   * 删除邮件标签
   *
   * @param id - 标签 ID
   * @sideeffect 从 tags 数组中移除该标签
   */
  async deleteTag(id: number) {
    await emailTagApi.delete(id);
    this.state.tags = this.state.tags.filter(t => t.id !== id);
    this.notify();
  },

  /**
   * 为邮件设置标签
   *
   * @param emailId - 邮件 ID
   * @param tagIds - 标签 ID 数组（替换该邮件的所有标签）
   * @sideeffect 更新 emails 中对应邮件的 tags 字段
   */
  async setEmailTags(emailId: number, tagIds: number[]) {
    await emailApi.setTags(emailId, tagIds);
    const index = this.state.emails.findIndex(e => e.id === emailId);
    if (index !== -1) {
      // 从本地 tags 列表中筛选出被选中的标签，赋值给邮件
      const email = { ...this.state.emails[index], tags: this.state.tags.filter(t => tagIds.includes(t.id)) };
      this.state.emails = [...this.state.emails.slice(0, index), email, ...this.state.emails.slice(index + 1)];
      this.notify();
    }
  },

  // ==================== 邮件签名 ====================

  /**
   * 获取指定邮箱账户的邮件签名列表
   *
   * @param accountId - 邮箱账户 ID
   * @sideeffect 更新 signatures[accountId] 为签名数组
   */
  async fetchSignatures(accountId: number) {
    try {
      const signatures = await signatureApi.list(accountId);
      this.state.signatures = { ...this.state.signatures, [accountId]: signatures };
      this.notify();
    } catch (error) {
      console.error('Failed to fetch signatures:', error);
    }
  },

  /**
   * 创建邮件签名
   *
   * @param accountId - 邮箱账户 ID
   * @param data - 签名创建请求数据
   * @returns 创建成功的签名对象
   * @sideeffect 将新签名添加到 signatures[accountId] 数组末尾
   */
  async createSignature(accountId: number, data: CreateEmailSignatureRequest) {
    const sig = await signatureApi.create(accountId, data);
    const current = this.state.signatures[accountId] || [];
    this.state.signatures = { ...this.state.signatures, [accountId]: [...current, sig] };
    this.notify();
    return sig;
  },

  /**
   * 更新邮件签名
   *
   * @param accountId - 邮箱账户 ID
   * @param signatureId - 签名 ID
   * @param data - 签名更新请求数据
   * @returns 更新后的签名对象
   * @sideeffect 更新 signatures[accountId] 中对应签名的数据
   */
  async updateSignature(accountId: number, signatureId: number, data: UpdateEmailSignatureRequest) {
    const sig = await signatureApi.update(accountId, signatureId, data);
    const current = this.state.signatures[accountId] || [];
    this.state.signatures = {
      ...this.state.signatures,
      [accountId]: current.map(s => s.id === signatureId ? sig : s),
    };
    this.notify();
    return sig;
  },

  /**
   * 删除邮件签名
   *
   * @param accountId - 邮箱账户 ID
   * @param signatureId - 签名 ID
   * @sideeffect 从 signatures[accountId] 中移除对应签名
   */
  async deleteSignature(accountId: number, signatureId: number) {
    await signatureApi.delete(accountId, signatureId);
    const current = this.state.signatures[accountId] || [];
    this.state.signatures = {
      ...this.state.signatures,
      [accountId]: current.filter(s => s.id !== signatureId),
    };
    this.notify();
  },

  /**
   * 激活指定签名（设为当前账户的默认签名）
   *
   * 激活后会将同账户下其他签名的 is_active 置为 0，
   * 然后从服务端重新获取当前激活签名以确保一致性。
   *
   * @param accountId - 邮箱账户 ID
   * @param signatureId - 要激活的签名 ID
   * @sideeffect 更新 signatures 中的 is_active 字段，刷新 activeSignature
   */
  async activateSignature(accountId: number, signatureId: number) {
    await signatureApi.activate(accountId, signatureId);
    const current = this.state.signatures[accountId] || [];
    // 本地更新：将目标签名设为激活，其余设为非激活
    this.state.signatures = {
      ...this.state.signatures,
      [accountId]: current.map(s => ({ ...s, is_active: s.id === signatureId ? 1 : 0 })),
    };
    // 从服务端获取激活签名的完整信息
    await this.fetchActiveSignature(accountId);
  },

  /**
   * 获取指定账户当前激活的签名
   *
   * @param accountId - 邮箱账户 ID
   * @returns 激活的签名对象，如果没有激活签名则返回 null
   * @sideeffect 更新 activeSignature[accountId]
   */
  async fetchActiveSignature(accountId: number) {
    try {
      const sig = await signatureApi.getActive(accountId);
      this.state.activeSignature = {
        ...this.state.activeSignature,
        // 服务端可能返回空对象，通过 sig.id 判断是否存在有效签名
        [accountId]: sig.id ? sig : null,
      };
      this.notify();
      return sig;
    } catch (error) {
      console.error('Failed to fetch active signature:', error);
      return null;
    }
  },

  /**
   * 上传签名中的图片
   *
   * @param accountId - 邮箱账户 ID
   * @param signatureId - 签名 ID
   * @param file - 要上传的图片文件
   * @returns 上传结果（含图片 URL）
   */
  async uploadSignatureImage(accountId: number, signatureId: number, file: File) {
    return signatureApi.uploadImage(accountId, signatureId, file);
  },
};

// ==================== Task Store ====================

/**
 * 任务 Store 状态接口
 *
 * 跟踪异步任务（如邮件抓取、批量发送）的执行状态。
 * 任务由 Arq worker 在后端异步执行，前端通过轮询 checkStatus() 跟踪进度。
 */
interface TaskState {
  /** 活跃任务映射表，key 为 task_id，value 为任务状态信息 */
  activeTasks: Map<string, TaskStatus>;
}

/**
 * 任务 Store
 *
 * 管理异步任务的状态跟踪。异步任务由后端 Arq worker 执行，
 * 前端通过 task_id 轮询状态接口获取执行结果。
 *
 * 典型流程：
 * 1. 调用 fetchEmails() 触发后端邮件抓取任务，获得 task_id
 * 2. 定时调用 checkStatus(task_id) 轮询任务状态
 * 3. 任务完成后调用 removeTask(task_id) 清理
 */
export const taskStore = {
  state: {
    activeTasks: new Map<string, TaskStatus>(),
  } as TaskState,

  /** 状态变更监听器列表 */
  listeners: [] as Array<(state: TaskState) => void>,

  /**
   * 订阅状态变更
   *
   * @param listener - 状态变更回调函数
   * @returns 取消订阅函数
   */
  subscribe(listener: (state: TaskState) => void) {
    this.listeners.push(listener);
    listener(this.state);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  },

  /** 通知所有监听器状态已变更 */
  notify() {
    this.listeners.forEach((listener) => listener(this.state));
  },

  /**
   * 触发邮件抓取任务
   *
   * 向后端提交邮件抓取请求，后端返回 task_id 用于后续状态查询。
   * 任务初始状态设为 'queued'，ready 为 false。
   *
   * @param accountId - 指定邮箱账户 ID（可选，不指定则抓取所有账户）
   * @param limit - 抓取邮件数量上限，默认 50
   * @returns 后端返回的任务信息（含 task_id）
   * @sideeffect 将新任务添加到 activeTasks Map 中
   */
  async fetchEmails(accountId?: number, limit: number = 50) {
    const response = await taskApi.fetchEmails({
      account_id: accountId,
      limit,
    });
    this.state.activeTasks.set(response.task_id, {
      task_id: response.task_id,
      status: 'queued',
      ready: false,
    });
    this.notify();
    return response;
  },

  /**
   * 查询异步任务状态
   *
   * @param taskId - 任务 ID
   * @returns 任务状态信息
   * @throws 查询失败时抛出错误
   * @sideeffect 更新 activeTasks 中对应任务的状态
   */
  async checkStatus(taskId: string) {
    try {
      const status = await taskApi.getStatus(taskId);
      this.state.activeTasks.set(taskId, status);
      this.notify();
      return status;
    } catch (error) {
      console.error('Failed to check task status:', error);
      throw error;
    }
  },

  /**
   * 移除已完成的任务
   *
   * 任务完成后从 activeTasks 中移除，避免 Map 无限增长。
   *
   * @param taskId - 要移除的任务 ID
   * @sideeffect 从 activeTasks 中删除该任务
   */
  removeTask(taskId: string) {
    this.state.activeTasks.delete(taskId);
    this.notify();
  },
};
